/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  UseDisclosureProps,
  ButtonGroup,
  ButtonProps,
  Alert,
  useToast,
  ToastId,
  useDisclosure,
  Heading,
  Text
} from '@chakra-ui/react';
import { useState, ReactElement, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  RaceTuningInput,
  Races,
  Cars,
} from 'api/generated/graphql';
import { RDrawer, RLoadingAnimation, RToast, RAlert, RCarCard, RBlockPaidRacesModal } from 'components';
import { Tyres, SetupSuggestions, Livery, RarityTier, AppliedLivery } from 'types';
import { IconArrow, IconCheckeredFlag, IconCheckWhite, IconEmptySet, IconGarage, IconGoldenTicket } from 'icons';
import {
  useTuningForRace,
  useEnterRace,
  useGetGarage,
  useGetRaceById,
  useRaceCountdown,
  useSetCarPart,
  useGetTicketsAvailable,
  useUserSettings,
  useSaveSavedSetups,
} from 'hooks';
import { useAnimatedTransition, useTuning } from 'context';
import DrawerPages from './DrawerPages';
import { racePreviewFooterGradient, racePreviewHeaderGradient, rarityLevels } from 'utils/themeConstants';
import { TicketTypeSelect } from './_components/TicketTypeSelect';
import { RaceHeader } from './_components/RaceHeader';
import { useRouter } from 'next/router';
import { TuningCarStats } from './_components/TuningCarStats';
import { isMobileDevice } from 'utils/helpers';
import { cardGradients } from 'components/Accordions/TrackAccordion/TrackAccordion';
import IconTicket from 'components/IconTicket/IconTicket';
import { RacePrizePool } from 'components/Races2/RacePrizePool/RacePrizePool';
import IconSilverTicket from 'components/IconSilverTicket/IconSilverTicket';
import { useRToast } from 'components/Toast/ToastConstructor';
import OptimalStats from './_components/OptimalStats';
import Forecast from './_components/Forecast';
import CarCard from 'components/CarCard/CarCard';
import RaceLevel from 'components/Races2/RaceLevel/RaceLevel';
import SelectedTag from 'components/RSelect/components/SelectedTag';
import RTyreList from './DrawerPages/SelectTyres';
import EngineAndTuning from './DrawerPages/SelectTyres';
import SavedSetupCarModal from './DrawerPages/SelectTyres';
import RaceEntryModal from 'components/Modals/RaceEntryModal/RaceEntryModal';


const NextButton = ({
  children,
  isDisabled,
  onClick,
  ...rest
}: ButtonProps) => {
  return (
    <Button
      w="full"
      isDisabled={isDisabled}
      textAlign="center"
      onClick={onClick}
      {...rest}
    >
      {children}
    </Button>
  );
};

type RaceDrawerProps = {
  raceId: string;
  carId?: string;
  idFromGarage?: string;
  source?: string;
};

export type PanelIdentifiers = 'selectCar' | 'selectTyres';

type DrawerPanelsType = {
  [key in PanelIdentifiers]?: JSX.Element;
};

// TODO consider a context for child props
const RacePreviewDrawer = ({
  raceId = '',
  carId,
  idFromGarage,
  isOpen = false,
  onClose = () => null,
  source,
}: UseDisclosureProps & RaceDrawerProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { getUserSettings } = useUserSettings();
  const { showAnimatedTransition } = useAnimatedTransition();
  const toast = useToast();
  const { showToast } = useRToast();
  const toastIdRef = useRef<ToastId | null>(null);

  const {
    isOpen: isOpenAdmissionUse,
    onOpen: onOpenAdmissionUse,
    onClose: onCloseAdmissionUse,
  } = useDisclosure();

  const {
    isOpen: isOpenRaceEntryModal,
    onOpen: onOpenRaceEntryModal,
    onClose: onCloseRaceEntryModal,
  } = useDisclosure();

  const {
    isOpen: isOpenBlockPaidRacesModal,
    onOpen: onOpenBlockPaidRacesModal,
    onClose: onCloseBlockPaidRacesModal,
  } = useDisclosure();

  // queries
  const { data: garageData, isLoading: isLoadingGarage } = useGetGarage();
  const {
    data: raceByIdData,
    isLoading: isLoadingRaceById,
    isSuccess: isSuccessRaceById,
  } = useGetRaceById({
    raceId,
  });
  const { data: getTicketsAvailabeData } = useGetTicketsAvailable();
  const racinoTicketsAvailable =
    getTicketsAvailabeData?.ticketsAvailable?.ticketsAvailable || 0;
  const tournamentTicketsAvailable =
    getTicketsAvailabeData?.ticketsAvailable?.tournamentTicketsAvailable || 0;
  const weeklyTournamentTicketsAvailable =
    getTicketsAvailabeData?.ticketsAvailable?.weeklyTournamentTicketsAvailable || 0;
  // mutations
  const { enterRace } = useEnterRace();
  const { tuningForRace } = useTuningForRace();
  const setCarPart = useSetCarPart();
  const { mutateAsync: saveSavedSetups, isLoading: isLoadingSave } =
    useSaveSavedSetups();

  // data prep
  const race = raceByIdData?.getRaceById?.race as Races;
  const raceTier = race?.tier.name.toLowerCase();
  const isDailyTournament = raceTier === 'tournament';
  const isWeeklyTournament = raceTier === 'weeklytournament';
  const isTournament = isDailyTournament || isWeeklyTournament;

  const ticketsAvailable = isDailyTournament
    ? tournamentTicketsAvailable
    : isWeeklyTournament
    ? weeklyTournamentTicketsAvailable
    : racinoTicketsAvailable;
  const numberOfTickets = race?.numberOfTickets || 0;
  const rawSuggestions = race?.setupSuggestions;
  const setupSuggestions: SetupSuggestions =
    rawSuggestions && JSON.parse(rawSuggestions);
  const carsInGarage = garageData?.getGarage as Cars[];
  const { raceStatus } = useRaceCountdown({
    currentStatus: race?.status,
    startTime: race?.startTime,
  });
  const {
    setCarId: setTuningCarId,
  } = useTuning();

  const userRaceData = race?.playersParticipants?.find((player) => {
    const isPlayerMatch = player.user.id === getUserSettings.data?.getUser.id;
    const isCarMatch = !carId || player.car.id === carId;

    return isPlayerMatch && isCarMatch;
  });

  const metricsForCar = raceByIdData?.getRaceById?.metricsForCar;
  const userCarMetrics = metricsForCar?.find((metric) => metric.carId === carId);

  // state
  const [panel, setPanel] = useState<PanelIdentifiers | null>(null);
  const [selectedCarId, setSelectedCarId] = useState<string | undefined>(
    userRaceData?.car?.id || carsInGarage?.[0]?.id || undefined
  );
  const [selectedTyres, setSelectedTyres] = useState<Tyres>(
    userRaceData?.tyres?.id as Tyres
  );
  const [withLeaderboardEntry, setWithLeaderboardEntry] = useState(false);
  const selectedCar = carsInGarage?.find((car) => car.id === selectedCarId);

  const [tuningSelections, setTuningSelections] = useState<RaceTuningInput>({
    raceId,
    carId: selectedCarId || '',
  });

  useEffect(() => {
    if (selectedCarId) {
      setTuningCarId(selectedCarId);
      setTuningSelections((oldState) => ({
        ...oldState,
        carId: selectedCarId,
      }));
    }
  }, [selectedCarId]);

  useEffect(() => {
    if (userRaceData?.tyres?.id) {
      setSelectedTyres(userRaceData?.tyres?.id as Tyres);
    }
  }, [userRaceData]);

  useEffect(() => {
    setSelectedCarId(userRaceData?.car.id || carsInGarage?.[0]?.id);
    console.log('userRaceData from useEffect', userRaceData);
    setTuningSelections((oldState) => ({
      ...oldState,
      speed: userCarMetrics?.metrics?.speed,
      braking: userCarMetrics?.metrics?.braking,
      handling: userCarMetrics?.metrics?.handling,
      stability: userCarMetrics?.metrics?.stability,
      acceleration: userCarMetrics?.metrics?.acceleration,
      fastTurns: userCarMetrics?.metrics?.fastTurns,
      slowTurns: userCarMetrics?.metrics?.slowTurns,
      cooling: userCarMetrics?.metrics?.cooling,
    }));
  }, [userRaceData, userCarMetrics]);

  const saveLastUsedSetup = async () => {
    if (!tuningSelections) return;

    const setupParts = {
      speed: tuningSelections.speed,
      slowTurns: tuningSelections.slowTurns,
      fastTurns: tuningSelections.fastTurns,
      cooling: tuningSelections.cooling,
      handling: tuningSelections.handling,
      braking: tuningSelections.braking,
      acceleration: tuningSelections.acceleration,
      stability: tuningSelections.stability,
      tyresId: selectedTyres,
    };

    await saveSavedSetups({
      SavedSetupsInput: {
        name: 'Last Used',
        carId: selectedCarId || '',
        ...setupParts,
      },
    });
  };

  // display any errors while joining race
  const [raceAlert, setRaceAlert] = useState<
    ReactElement<typeof Alert> | undefined
  >(undefined);
  const [userInRace, setUserInRace] = useState<boolean | null>(null);

  const canEnterRace =
    !userInRace && (raceStatus === 'open' || raceStatus === 'tuning');
  const canTune =
    userInRace && (raceStatus === 'open' || raceStatus === 'tuning');

  const isMobile = isMobileDevice()

  useEffect(() => {
    const enterRaceError = enterRace?.error as { message: string };
    if (enterRaceError) {
      const braceIndex = enterRaceError?.message.indexOf('{');
      let msg = ''
      if(enterRaceError?.message.includes("Your location doesn't allow entering paid races.")) {
        onOpenBlockPaidRacesModal();
        return
      } else if (braceIndex > -1) {
        msg = enterRaceError?.message.substring(0, braceIndex - 2);
      } else {
        msg = 'Error when trying to join the race'
      }

      showToast({ variant: 'error', title: msg });

    }
  }, [enterRace?.error]);

  useEffect(() => {
    if ((race?.userInRace && !isTournament) || carId || source === 'entries') {
      setUserInRace(true);
      setPanel('selectTyres');
    }
    if (!race?.userInRace || (race?.userInRace && isTournament && source === 'tournament')) {
      setUserInRace(false);
      setPanel('selectCar');
    }
  }, [race?.userInRace, isOpen, source]);

  const isEligibleForRace = () => {
    const difficulty = race?.difficulty || 0;
    const rarity = selectedCar?.rarity;
    if (
      (difficulty >= 2 && ['trial', 'common'].includes(rarity || '')) ||
      (difficulty >= 3 && rarity === 'uncommon') ||
      (difficulty >= 4 && rarity === 'rare') ||
      (difficulty >= 5 && rarity === 'epic')
    )
      return false;
    else return true;
  };

  const getPanelKey = (
    currentPanel: PanelIdentifiers,
    change: number
  ): PanelIdentifiers => {
    const flow: PanelIdentifiers[] = ['selectCar', 'selectTyres'];

    const currentIndex = flow.indexOf(currentPanel);

    let nextIndex = currentIndex + change;
    nextIndex = Math.max(0, Math.min(flow.length - 1, nextIndex));

    if (change > 0) {
      if (currentPanel === 'selectCar' && canTune) {
        return 'selectTyres';
      }
      if (currentPanel === 'selectCar' && ticketsAvailable >= numberOfTickets) {
        return 'selectTyres';
      }
    }
    if (change < 0) {
      if (currentPanel === 'selectTyres') {
        return 'selectCar';
      }
    }

    return flow[nextIndex];
  };

  const updatePanel = (change: number) => {
    if (!panel) return;

    const nextPanelKey = getPanelKey(panel, change);
    if (nextPanelKey) {
      document.getElementById('drawerBody')?.scrollTo(0, 0);
      setPanel(nextPanelKey);

      queryClient.refetchQueries({
        queryKey: ['ticketsAvailable'],
      });
    }
  };

  const nextPanel = () => updatePanel(1);

  const renderPanel = (
    isLoading: boolean,
    PanelComponent: (props: any) => JSX.Element,
    props: any
  ) =>
    isLoading ? (
      <Flex w="full" h="full" alignItems="center" justifyContent="center">
        <RLoadingAnimation />
      </Flex>
    ) : (
      <PanelComponent {...props} />
    );

  const allPanels = {
    selectCar: renderPanel(
      isLoadingGarage && enterRace.isLoading,
      DrawerPages.SelectCar,
      {
        admissionFee: numberOfTickets,
        cars: carsInGarage,
        raceAlert,
        race: race,
        raceStartTime: race?.startTime,
        isOpen: isOpenAdmissionUse,
        withLeaderboardAvailable:
          raceByIdData?.getRaceById?.withLeaderboardAvailable,
        withLeaderboardEntry,
        setWithLeaderboardEntry,
        selectedCarId,
        setSelectedCarId,
        onClose: onCloseAdmissionUse,
        idFromGarage,
      }
    ),
    selectTyres: renderPanel(
      isLoadingGarage || isLoadingRaceById,
      DrawerPages.SelectTyres,
      {
        selectedTyres,
        setSelectedTyres,
        carId: selectedCarId,
        mutateCarPart: setCarPart.mutate,
        tuningSelections,
        setTuningSelections,
        parts: userRaceData,
        mutateTuning: tuningForRace.mutate,
        tuningError: tuningForRace.error,
        setupSuggestions,
        carStats: selectedCar?.stats,
      }
    ),
  };

  let drawerPanels: DrawerPanelsType = allPanels;

  if (canTune) {
    drawerPanels = {
      selectTyres: allPanels.selectTyres,
    };
  }

  const isEnteringRace =
    selectedCarId &&
    raceId &&
    panel === 'selectCar' &&
    ticketsAvailable >= numberOfTickets;

  let buttonText = 'Next';
  if (panel === 'selectTyres') {
    buttonText = 'Tune & Go!';
  }

  const numberOfTicketsAndTournament = withLeaderboardEntry ? numberOfTickets + 1 : numberOfTickets
  if (panel === 'selectCar') {
    buttonText = `Join Race and Continue`;
  }

  let loadingText = '';
  if (enterRace.isLoading) {
    loadingText = 'Entering Race...';
  }

  if (
    panel === 'selectTyres' &&
    (setCarPart.isLoading ||
      tuningForRace.isLoading ||
      isLoadingGarage ||
      isLoadingSave)
  ) {
    loadingText = 'Saving Setup...';
  }

    // Check localStorage and show guide modal on first visit
    useEffect(() => {
      const hasSeenRaceEntryModal = localStorage.getItem('hasSeenRaceEntryModal');
      if (!hasSeenRaceEntryModal) {
        onOpenRaceEntryModal();
      }
    }, []);
  
    // Handle the guide modal close
    const handleRaceEntryModalClose = useCallback(() => {
      localStorage.setItem('hasSeenRaceEntryModal', 'true');
      onCloseRaceEntryModal();
    }, []);


  return (
    <RDrawer
      drawerBodyProps={{
        p: garageData?.getGarage.length === 0 ? '0' : { base: '2', md: '6' },
        py: garageData?.getGarage.length === 0 ? '0' : { base: 2, md: 4 },
      }}
      drawerContentProps={{
        bg: 'rgba(18, 18, 18, 1)',
      }}
      drawerHeaderProps={{
        bg: racePreviewHeaderGradient,
        borderBottomLeftRadius: '1.25rem',
        px: { base: '0.75rem', md: '1rem' },
        py: '0.75rem',
        pb: '0',
        justifyContent: 'center',
      }}
      closeButtonProps={{
        ml: {
          base: '0.5rem',
          md: 'auto',
        },
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        _hover: {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        transition: 'background-color 0.3s ease',
      }}
      drawerFooterProps={{
        bg: racePreviewFooterGradient,
        borderTop: '1.5px solid rgba(255, 255, 255, 0.1)',
        borderTopRightRadius: '1.25rem',
        padding: isMobile ? '0.75rem 1rem 0.75rem' : 'auto',
      }}
      heading={<RaceHeader race={race} raceStatus={raceStatus} />}
      headingProps={{
        w: {
          base: 'full',
          md: 'auto',
        },
      }}
      gradient={true}
      gradientColor={cardGradients[race?.track?.frontendName || '']}
      headerContent={
        !isLoadingRaceById &&
        race && (
          panel === 'selectCar' ? 
          <OptimalStats 
            raceLevel={race.tier.name.toLowerCase() === 'tournament' || race.tier.name.toLowerCase() === 'weeklytournament' ? 6 : race.difficulty} 
            stats={race?.track?.trackStats} 
          /> 
          : <Forecast race={race} />
        )
      }
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setWithLeaderboardEntry(false);
        setPanel('selectCar');
        if (router.query.car) {
          router.replace({
            pathname: router.pathname,
            query: {
              ...router.query,
              car: undefined,
            },
          }, undefined, { shallow: true })
        }
      }}
      drawerBody={
        <Flex
          pos="relative"
          flexDirection="column"
          width="full"
          overflowY="auto"
          overflowX="hidden"
          className="grey-scrollbar"
        >
          {isLoadingRaceById ||
          isLoadingGarage ||
          userInRace === null ||
          !panel ? (
            <Flex w="full" h="full" alignItems="center" justifyContent="center">
              <RLoadingAnimation />
            </Flex>
          ) : !carsInGarage?.[0] ? (
            <Flex 
              w="full" 
              h="full" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              gap="2"
              p="4"
              background="darkVoid.100"
            >
              <IconEmptySet />
              <Text
                fontSize="24px"
                lineHeight="32px"
                fontFamily="heading"
                fontWeight={400}
                textTransform="uppercase"
                color="white.95"
                textAlign="center"
              >
                Generating your new ride!
              </Text>

              <Text
                fontSize="16px"
                lineHeight="24px"
                fontFamily="body"
                color="white.95"
                textAlign="center"
              >
                We&apos;re generating your Level 1 car - it&apos;ll be in your garage shortly.
              </Text>

              <Button
                variant="primary"
                w="full"
                gap="2"
                onClick={() => {
                  router.push('/garage');
                }}
              >
                <IconGarage mb="0.5" />
                GO TO GARAGE
              </Button>
            </Flex>
          ) : (
            drawerPanels[panel]
          )}
        </Flex>
      }
      drawerFooter={
        <>
          {isSuccessRaceById && panel === 'selectCar' && (
            <Flex w="full" justifyContent="space-between" mb="4" px="4">
              <Flex
                flexDir="column"
                alignItems="center"
                mr={{ base: 16, md: 16 }}
                justifyContent="flex-end"
              >
                {numberOfTicketsAndTournament > 0 ? (
                  <>
                    <Flex
                      alignItems="center"
                      gap="1"
                    >
                      <Text
                        fontSize={{ base: '32px', md: '48px' }}
                        lineHeight={{ base: '32px', md: '48px' }}
                        fontWeight="600"
                        color="white"
                      >
                        {numberOfTicketsAndTournament}
                      </Text> 
                      {race.tier.name.toLowerCase() === 'tournament' 
                          ? <IconSilverTicket /> 
                          : race.tier.name.toLowerCase() === 'weeklytournament'
                          ? <IconGoldenTicket 
                            width={{ base: '2rem', md: '2.25rem' }}
                            height={{ base: '2rem', md: '2.25rem' }}
                          />
                          : <IconTicket
                            width={{ base: '2rem', md: '2.25rem' }}
                            height={{ base: '2rem', md: '2.25rem' }}
                          />
                        }
                    </Flex>
                  </>
                ) : (
                  <Text
                    fontSize="2rem"
                    lineHeight="100%"
                    fontWeight="600"
                    color="white.100"
                  >
                    FREE
                  </Text>
                )}
                <Text
                  fontSize="10px"
                  lineHeight="12px"
                  fontWeight="600"
                  color="cerebralGrey.100"
                >
                  ENTRY
                </Text>
              </Flex> 

              <Flex>
                <RacePrizePool
                  raceId={race?.id}
                  prizePool={race?.prizePool}
                  fontSize={{ base: '32px', md: '42px' }}
                  showText
                />
              </Flex>
            </Flex>
          )}

          {isSuccessRaceById && panel === 'selectTyres' && (
            <Flex w="full" justifyContent="space-between" mb="4">
              <Flex
                alignItems="center"
                mr={{ base: 8, md: 16 }}
                gap={{ base: 2, md: 4 }}
              >
                <RCarCard
                  carId={selectedCarId}
                  hasInsetBorder={false}
                  locked={selectedCar?.locked}
                  livery={(selectedCar?.livery || '') as Livery}
                  rarity={selectedCar?.rarity as RarityTier}
                  appliedLivery={(selectedCar?.appliedLivery || '') as AppliedLivery}
                  backgroundPreload
                  hasBackground
                  mb="0"
                  imageProps={{
                    paddingTop: '0.5rem',
                    transform: 'scale(0.8)',
                  }}
                  w={{ base: '7rem', md: '7rem' }}
                  h="full"
                  cursor="pointer"
                />

                <Flex flexDir="column" gap="0.25rem">
                  <Flex>
                    <Text
                      fontFamily="heading"
                      fontSize={{ base: '1rem', md: '1.125rem'}}
                      lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                      color="white.95"
                      fontWeight={400}
                    >
                      {selectedCar?.name}
                    </Text>
                  </Flex>

                  <Flex width="fit-content" height="fit-content" alignSelf="flex-start">
                    <RaceLevel 
                      level={rarityLevels[selectedCar?.rarity as RarityTier]}
                    />
                  </Flex>
                </Flex>
                  
              </Flex> 

              <Flex width="fit-content" height="fit-content" alignSelf="center">
                <SelectedTag />
              </Flex>

            </Flex>
          )}

          {isSuccessRaceById && (canEnterRace || canTune) && (
            <ButtonGroup w="full">
              <NextButton
                isLoading={Boolean(loadingText)}
                loadingText={loadingText}
                id="bottom-drawer-button"
                borderRadius="0.25rem"
                fontSize="14.5px"
                lineHeight="18px"
                isDisabled={
                  isLoadingGarage ||
                  (panel === 'selectCar' && selectedCar?.locked) ||
                  !isEligibleForRace()
                  || carsInGarage[0] === undefined
                }
                variant={'tertiary-gradient'}
                onClick={async () => {
                  if (isEnteringRace) {
                    // clear any existing errors
                    setRaceAlert(undefined);

                    const res = await enterRace.mutateAsync({
                      enterRaceInput: {
                        carId: selectedCarId,
                        raceId,
                        withLeaderboardEntry,
                      },
                    });

                    if (res.enterRace.status === 'accepted') {
                      showAnimatedTransition({
                        Icon: IconCheckeredFlag,
                        title: 'race',
                        id: 'race-entered-success',
                        subtitle: 'entered',
                        color: 'bloodMoon.100',
                        bgColor:
                          'linear-gradient(81.73deg, #008A8A -122.13%, #000000 46.69%, #000000 57.14%, #D83832 212.04%)',
                        gradientColor: {
                          from: 'rgba(216, 56, 50, 0.2)',
                          to: 'rgba(216, 56, 50, 0)',
                        },
                      });
                      nextPanel();
                      setUserInRace(true);
                    }

                    queryClient.refetchQueries({
                      queryKey: ['nextUpRacesByTierAndStatusQuery'],
                    });
                  }

                  if (panel === 'selectTyres') {
                    await saveLastUsedSetup();
                    if (router.query.car) {
                      router.replace({
                        pathname: router.pathname,
                        query: {
                          ...router.query,
                          car: undefined,
                        },
                      }, undefined, { shallow: true })
                    }
                    router.push(`/races/${race?.id}`);
                  }

                  if (panel === 'selectCar' && !isEnteringRace) {
                    if (race?.tier?.name?.toLowerCase() === 'tournament') {
                      return (toastIdRef.current = toast({
                        position: 'bottom-right',
                        render: () => (
                          <RToast
                            variant="error"
                            title={`You must have at least ${race.numberOfTickets} tournament ticket(s) to enter this race`}
                            onClose={() =>
                              toast.close(toastIdRef.current as ToastId)
                            }
                          />
                        ),
                      }));
                    }

                    onOpenAdmissionUse();
                    return;
                  }

                  if (!isEnteringRace && panel !== 'selectTyres') {
                    nextPanel();
                  }
                }}
                // className="pulse-border-grey"
              >
                {/* <span className="pulse-overlay-grey" /> */}
                {buttonText}
                {buttonText === 'Ready to Race' &&
                  <IconCheckWhite 
                    ml="0.4rem" 
                    mb="0.15rem"
                    width="1rem"
                    height="1rem"
                    __css={{
                      path: {
                        fill: 'white.80',
                      },
                    }}
                  />}
                {buttonText === 'Join Race and Continue' &&
                  <IconCheckWhite 
                    ml="0.4rem" 
                    mb="0.15rem"
                    width="1rem"
                    height="1rem"
                    __css={{
                      path: {
                        fill: 'white.80',
                      },
                    }}
                  />}
                {buttonText === 'Tune & Go!' && (
                  <IconCheckWhite 
                    ml="0.4rem"
                    mb="0.15rem"
                    width="1rem"
                    height="1rem"
                    __css={{
                      path: {
                        fill: 'white.80',
                      },
                    }}
                  />
                )}
              </NextButton>
            </ButtonGroup>
          )}
          <RaceEntryModal 
            isOpen={isOpenRaceEntryModal}
            onClose={handleRaceEntryModalClose}
          />
          <RBlockPaidRacesModal 
            isOpen={isOpenBlockPaidRacesModal}
            onClose={onCloseBlockPaidRacesModal}
          />
        </>
      }
    />
  );
};

export default RacePreviewDrawer;